import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import type { UserSession } from '../model/types'

import { goauthHttp } from './goauth-http'
import { tokenSchema, type TokenSet } from './token-api'

/** `current` is the session the presented token belongs to, by its `sid` claim. */
const sessionsSchema = z.object({
  sessions: z.array(
    z.object({
      id: z.string(),
      ip: z.string().optional(),
      user_agent: z.string().optional(),
      created_at: z.string(),
      expires_at: z.string(),
      current: z.boolean(),
    }),
  ),
})

export async function fetchSessions(signal?: AbortSignal): Promise<UserSession[]> {
  const raw = await goauthHttp.get('auth/sessions', { schema: sessionsSchema, signal })

  return raw.sessions.map((session) => ({
    id: session.id,
    ip: session.ip ?? null,
    userAgent: session.user_agent ?? null,
    createdAt: session.created_at,
    expiresAt: session.expires_at,
    isCurrent: session.current,
  }))
}

export const sessionsQuery = () =>
  queryOptions({
    queryKey: ['auth-sessions'],
    queryFn: ({ signal }) => fetchSessions(signal),
  })

/**
 * Changing a password destroys every session, the caller's included, so goauth
 * hands back a fresh pair in the same response. Storing it is not an
 * optimisation — without it the user is signed out by their own action.
 *
 * The reply carries no `user`, which is why those fields are optional on the
 * schema shared with the token endpoint.
 */
export const changePassword = (currentPassword: string, newPassword: string): Promise<TokenSet> =>
  goauthHttp.post('auth/password', {
    json: { current_password: currentPassword, new_password: newPassword },
    schema: tokenSchema,
  })

/** Takes no body: the user comes from the bearer token. Ends the current session too. */
export const revokeAllSessions = (): Promise<void> => goauthHttp.post('auth/logout-all')
